import { Inject, Injectable } from '@angular/core';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { MarketplacePackagesApiService, Status } from '@vendasta/marketplace-packages';
import { PackageInterface } from '@vendasta/marketplace-packages/lib/_internal/interfaces/package.interface';
import { Observable, map, of, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';

export interface ExpectedExtraData {
  includeAny: boolean;
}

@Injectable({ providedIn: 'root' })
export class PackageSelectorService implements AutomationReplacementChipsDataService<PackageInterface> {
  supportsSearching = true;

  anyPackage = {
    packageId: '*',
    name: 'AUTOMATIONS.EDITOR.TRIGGERS.CUSTOMER_VIEWS_PACKAGE.ANY_PACKAGE',
  };

  constructor(
    private readonly packageService: MarketplacePackagesApiService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {}

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData: ExpectedExtraData,
  ): Observable<PagedResponse<PackageInterface>> {
    return this.namespace$.pipe(
      switchMap((partnerId) => {
        return this.packageService.listPackages({
          partnerId: partnerId,
          statuses: [Status.PUBLISHED],
          sort: false,
          pageSize: pageSize,
          cursor: cursor,
          filterTerm: searchTerm,
        });
      }),
      take(1),
      map((resp) => {
        if (extraData?.includeAny && !cursor) {
          resp.packages.unshift(this.anyPackage as any);
        }
        return resp;
      }),
      map((resp) => {
        return {
          results: resp.packages,
          hasMore: resp.hasMore,
          nextCursor: resp.nextCursor,
        };
      }),
    );
  }

  convertDataToDisplay(s: PackageInterface): Observable<string> {
    return of(s.name);
  }

  convertDataToFormValue(x: PackageInterface): any {
    return x.packageId;
  }

  getMulti(x: string[]): Observable<PackageInterface[]> {
    const anyPackageIndex = (x || []).indexOf('*');
    return this.packageService
      .getMultiPackages({
        packageIds: x,
      })
      .pipe(
        map((resp) => resp.packageContainers.map((c) => c?.package)),
        map((packages) => {
          if (anyPackageIndex > -1) {
            packages[anyPackageIndex] = this.anyPackage as any;
          }
          return packages;
        }),
      );
  }
}
